<template>
  <!-- 公共辅料选择弹窗：打开时加载辅料列表，确认时返回完整选中项 -->
  <el-dialog class="material-dialog" :title="title || $t('message.materialList')" :visible.sync="visible" width="1320px" append-to-body
    :close-on-click-modal="false">
    <!-- 辅料名称、订单号及分类搜索区 -->
    <div class="material-search">
      <el-input v-model.trim="searchForm.name" :placeholder="$t('message.pleaseEnterMaterialName')" clearable
        @keyup.enter.native="search" />
      <el-input v-model.trim="searchForm.order_sn" :placeholder="$t('message.orderNumber')" clearable
        @keyup.enter.native="search" />
      <el-select v-model="searchForm.type_id" :placeholder="$t('message.materialType')" clearable filterable>
        <el-option v-for="item in typeList" :key="item.id" :label="item.name" :value="item.id" />
      </el-select>
      <el-button type="primary" :loading="loading" @click="search">{{ $t('message.search') }}</el-button>
      <el-button @click="reset">{{ $t('message.reset') }}</el-button>
    </div>

    <!-- 辅料列表：点击行或复选框切换选择，图片和名称不触发行选择 -->
    <div class="material-list">
      <el-table v-loading="loading" :data="list" row-key="id" border max-height="500"
        :empty-text="$t('message.noMaterial')" :header-cell-style="{ background: '#EDEFF4' }" @row-click="toggle">
        <el-table-column align="center" width="54">
          <template slot-scope="scope">
            <el-checkbox class="material-checkbox" :value="isSelected(scope.row)" @click.native.stop @change="toggle(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column prop="id" :label="$t('message.materialId')" align="center" width="90" />
        <el-table-column :label="$t('message.mainImage')" align="center" width="100">
          <template slot-scope="scope">
            <div class="material-image" @click.stop>
              <el-image v-if="scope.row.main_image" :src="scope.row.main_image" fit="cover"
                :preview-src-list="[scope.row.main_image]" />
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.materialNameTrans')" min-width="180">
          <template slot-scope="scope">
            <div class="material-name" :title="scope.row.name_trans" @click.stop="openMaterialDetail(scope.row)">{{ scope.row.name_trans }}</div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.type')" min-width="130">
          <template slot-scope="scope">{{ getMaterialTypeName(scope.row) }}</template>
        </el-table-column>
        <el-table-column :label="$t('message.status')" align="center" width="120">
          <template slot-scope="scope">
            <!-- 沿用调用页面的库存状态文案和颜色规则 -->
            <span v-if="showStatusName" class="stock-status" :class="statusNameClass(scope.row)">{{ scope.row.status_name }}</span>
            <span v-else class="stock-status" :class="stockStatusClass(scope.row)">{{ stockStatusText(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.quantity')" align="center" width="110">
          <template slot-scope="scope">{{ formatNum(scope.row.material_stock_num) }}</template>
        </el-table-column>
        <!-- 辅料所在订单沿用辅料列表字段；订单链接不触发行选择 -->
        <el-table-column :label="$t('message.materialInOrder')" min-width="260">
          <template slot-scope="scope">
            <div class="orders-cell">
              <div v-for="(order, orderIndex) in getOrderList(scope.row)" :key="orderIndex" class="order-card">
                <router-link :to="`/orderPaymentDetail?order_sn=${order.order_sn}`" target="_blank"
                  class="order-sn" @click.native.stop>
                  {{ order.order_sn }}
                </router-link>
                <div>
                  <span>{{ order.sorting }}{{ $t('message.mash') }}</span>
                  <span class="order-points">
                    {{ $t('message.remainingPoints') }}
                    <b>{{ order.send_await_num }}</b>
                    {{ $t('message.point') }}
                  </span>
                </div>
              </div>
              <div v-if="!getOrderList(scope.row).length">-</div>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 取消关闭弹窗；二次确认后返回选中辅料 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="visible = false">{{ $t('message.cancel') }}</el-button>
      <el-button type="primary" @click="confirm">{{ $t('message.confirmMarkAsMaterial') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
/**
 * 公共辅料选择弹窗。
 * 列表数据直接使用 warehouseApi.materialList 返回字段；confirm 事件返回选中的完整辅料对象。
 */
export default {
  name: 'MaterialSelectDialog',
  props: {
    // 当前客户 ID，辅料列表必须按客户筛选
    userId: {
      default: ''
    },
    // 自定义弹窗标题
    title: {
      default: ''
    },
    // false 单选，true 多选
    multiple: {
      default: false
    },
    showStatusName: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      list: [],
      selectedList: [],
      typeList: [],
      // 参数与辅料列表接口保持一致
      searchForm: {
        user_id: '',
        name: '',
        order_sn: '', // 订单号筛选
        type_id: '',
        page: 1,
        pageSize: 999
      }
    }
  },
  methods: {
    // 对外打开方法，可传入已选辅料用于回显
    open(selectedList = []) {
      if (!this.userId) {
        this.$message.warning(this.$t('message.customerIdRequired'))
        return
      }
      this.searchForm = {
        user_id: this.userId,
        name: '',
        order_sn: '',
        type_id: '',
        page: 1,
        pageSize: 999
      }
      this.selectedList = selectedList.slice()
      this.visible = true
      this.materialTypeList()
      this.materialList()
    },
    // 分类数据与辅料列表保持一致，仅在打开弹窗时加载。
    materialTypeList() {
      this.$api.warehouseApi.materialTypeList().then(res => {
        this.typeList = res.data || []
      })
    },
    // 名称、订单号和分类搜索时回到第一页
    search() {
      this.searchForm.page = 1
      this.materialList()
    },
    reset() {
      this.searchForm.name = ''
      this.searchForm.order_sn = ''
      this.searchForm.type_id = ''
      this.searchForm.page = 1
      this.materialList()
    },
    // 获取辅料列表，保留接口返回的完整对象供确认后回传
    materialList() {
      this.loading = true
      this.$api.warehouseApi.materialList(this.searchForm).then(res => {
        const data = res.data || {}
        this.list = data.list || data.data || []
      }).finally(() => {
        this.loading = false
      })
    },
    isOutOfStock(item) {
      return Number(item.material_stock_num || 0) === 0
    },
    isStockInsufficient(item) {
      return Number(item.material_stock_num || 0) < Number(item.pre_consume_num || 0)
    },
    stockStatusText(item) {
      if (this.isOutOfStock(item)) return this.$t('message.noStock')
      if (this.isStockInsufficient(item)) return this.$t('message.stockInsufficient')
      if (Number(item.pre_up_num || 0) > 0) return this.$t('message.suggestReplenishment')
      return this.$t('message.stockSufficient')
    },
    stockStatusClass(item) {
      if (this.isOutOfStock(item) || this.isStockInsufficient(item)) return 'is-danger'
      if (Number(item.pre_up_num || 0) > 0) return 'is-warning'
      return 'is-success'
    },
    // 付款后订单互换补货提示与库存充足的展示颜色，缺货状态仍保持红色。
    statusNameClass(item) {
      if (this.isOutOfStock(item) || this.isStockInsufficient(item)) return 'is-danger'
      if (Number(item.pre_up_num || 0) > 0) return 'is-success'
      return 'is-warning'
    },
    getMaterialTypeName(item) {
      return item.type_name || (item.type && item.type.name) || '-'
    },
    formatNum(num) {
      return num != null && num !== '' ? num : '-'
    },
    isSelected(item) {
      return this.selectedList.some(selected => selected.id === item.id)
    },
    // 单选时替换当前项，多选时追加或移除当前项
    toggle(item) {
      const index = this.selectedList.findIndex(selected => selected.id === item.id)
      if (this.multiple) {
        if (index === -1) {
          this.selectedList.push(item)
        } else {
          this.selectedList.splice(index, 1)
        }
        return
      }
      this.selectedList = index === -1 ? [item] : []
    },
    // 确认后直接返回选中的辅料列表完整数据
    confirm() {
      if (!this.selectedList.length) {
        this.$message.warning(this.$t('message.pleaseSelectMaterial'))
        return
      }
      this.$confirm(
        this.$t('message.confirmMarkSelectedAsMaterial'),
        this.$t('message.prompt'),
        {
          confirmButtonText: this.$t('message.ok'),
          cancelButtonText: this.$t('message.cancel'),
          type: 'warning'
        }
      ).then(() => {
        this.$emit('confirm', this.selectedList.slice())
        this.visible = false
      }).catch(() => {})
    },
    // 与辅料列表一致，读取接口返回的辅料所在订单明细
    getOrderList(item) {
      if (Array.isArray(item.detail_list)) return item.detail_list
      if (Array.isArray(item.order_list)) return item.order_list
      return []
    },
    // 点击辅料名称新开辅料详情
    openMaterialDetail(item) {
      const id = item.id || item.material_id
      if (!id) return
      const route = this.$router.resolve({
        path: '/materialDetail',
        query: {
          id,
          user_id: this.userId || item.user_id || ''
        }
      })
      window.open(route.href, '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
// 新增订单列后预留展示宽度，窄屏限制在视口内并由表格横向滚动
.material-dialog /deep/ .el-dialog {
  max-width: calc(100vw - 40px);
}

.material-search {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;

  > .el-input {
    width: 240px;
  }

  .el-select {
    width: 200px;
  }
}

// 列表沿用弹窗原有滚动高度，不改变确认按钮的位置
.material-list {
  min-height: 260px;
}

.material-image {
  width: 64px;
  height: 64px;
  margin: 0 auto;
  border-radius: 4px;
  background-color: #9b9b9b;
  overflow: hidden;

  .el-image {
    width: 100%;
    height: 100%;
  }
}

.material-name {
  color: #303133;
  line-height: 22px;
  word-break: break-all;
  cursor: pointer;

  &:hover {
    color: #c8383e;
  }
}

.stock-status {
  font-weight: bold;

  &.is-danger {
    color: #c8383e;
  }

  &.is-warning {
    color: #e6a23c;
  }

  &.is-success {
    color: #15ad3c;
  }
}

// 选择列不继承业务页面的双倍缩放，去掉默认右边距，避免勾选框偏移和裁切
.material-checkbox {
  display: inline-flex;
  align-items: center;
  margin: 0;
  min-width: 0;
  min-height: 0;
  line-height: 1;
  vertical-align: middle;

  /deep/ .el-checkbox__inner {
    width: 20px;
    height: 20px;
    transform: none !important;

    &::after {
      top: 2px;
      left: 6px;
      width: 5px;
      height: 10px;
    }
  }
}

/deep/ .el-checkbox__input.is-checked .el-checkbox__inner {
  border-color: #c8383e;
  background-color: #c8383e;
}

// 辅料所在订单沿用列表样式，长订单号可换行
.orders-cell {
  .order-card {
    padding: 8px 10px;
    margin-bottom: 8px;
    background: #f7f7f7;
    border-radius: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .order-sn {
    display: block;
    margin-bottom: 4px;
    color: #b4272d;
    font-weight: 600;
    text-decoration: underline;
    word-break: break-all;
  }

  .order-points {
    margin-left: 12px;

    b {
      color: #b4272d;
    }
  }
}

.dialog-footer {
  text-align: center;
}
</style>
